import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  SelectChangeEvent,
  Tabs,
  Tab
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAppSelector, useAppDispatch } from '../store/hooks.ts';
import { selectCurrentOrganization, setCurrentOrganization } from '../store/organization/organizationSlice.ts';
import {
  getOrganizationMembers,
  addMemberToOrganization,
  removeMemberFromOrganization,
  updateMemberRole,
  updateOrganization,
  OrganizationMember
} from '../api.ts';
import APIKeyManagement from './APIKeyManagement.tsx';
import BucketConfigManagement from './BucketConfigManagement.tsx';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ROLES = ['owner', 'admin', 'member', 'viewer'];

const OrganizationManagement = () => {
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add member dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');

  // Edit role dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<OrganizationMember | null>(null);
  const [editRole, setEditRole] = useState('');

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingMember, setDeletingMember] = useState<OrganizationMember | null>(null);

  const loadMembers = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getOrganizationMembers(currentOrganization.id);
      setMembers(data);
    } catch (err: any) {
      console.error('Failed to load members:', err);
      setError(err.response?.data?.detail || 'Failed to load organization members');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      loadMembers();
    }
  }, [currentOrganization, loadMembers]);

  const handleAddMember = async () => {
    if (!currentOrganization?.id || !newMemberEmail.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await addMemberToOrganization(currentOrganization.id, newMemberEmail, newMemberRole);
      setSuccess('Member invited successfully');
      setShowAddDialog(false);
      setNewMemberEmail('');
      setNewMemberRole('member');
      await loadMembers();
    } catch (err: any) {
      console.error('Failed to add member:', err);
      setError(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!currentOrganization?.id || !editingMember || !editingMember.user_id) return;

    try {
      setLoading(true);
      setError(null);
      await updateMemberRole(currentOrganization.id, editingMember.user_id, editRole);
      setSuccess('Role updated successfully');
      setShowEditDialog(false);
      setEditingMember(null);
      await loadMembers();
    } catch (err: any) {
      console.error('Failed to update role:', err);
      setError(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!currentOrganization?.id || !deletingMember || !deletingMember.user_id) return;

    try {
      setLoading(true);
      setError(null);
      await removeMemberFromOrganization(currentOrganization.id, deletingMember.user_id);
      setSuccess('Member removed successfully');
      setShowDeleteDialog(false);
      setDeletingMember(null);
      await loadMembers();
    } catch (err: any) {
      console.error('Failed to remove member:', err);
      setError(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (member: OrganizationMember) => {
    setEditingMember(member);
    setEditRole(member.role);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (member: OrganizationMember) => {
    setDeletingMember(member);
    setShowDeleteDialog(true);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'member': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const isAdmin = currentOrganization?.role === 'admin' || currentOrganization?.role === 'owner';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!currentOrganization) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
        <Alert severity="warning">
          Please select an organization to manage.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Organization Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentOrganization.name}
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="organization tabs">
              <Tab label="Members" id="org-tab-0" aria-controls="org-tabpanel-0" />
              <Tab label="API Keys" id="org-tab-1" aria-controls="org-tabpanel-1" />
              <Tab label="Storage Buckets" id="org-tab-2" aria-controls="org-tabpanel-2" />
              <Tab label="Modules" id="org-tab-3" aria-controls="org-tabpanel-3" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Team Members
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setShowAddDialog(true)}
                  disabled={loading}
                >
                  Invite User
                </Button>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {!isAdmin && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You need admin or owner role to manage organization members.
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Default Org</TableCell>
                  {isAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                      No members found
                    </TableCell>
                  </TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow key={member.user_id || member.email}>
                      <TableCell>
                        {member.pending ? (
                          <Chip label="Pending" size="small" color="warning" />
                        ) : (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {member.user_id?.substring(0, 8)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{member.name || '-'}</TableCell>
                      <TableCell>
                        {member.email || '-'}
                        {member.pending && (
                          <Chip
                            label="Invite sent"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.role}
                          color={getRoleColor(member.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {member.is_default ? (
                          <Chip label="Default" size="small" color="success" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          {!member.pending ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => openEditDialog(member)}
                                disabled={loading}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => openDeleteDialog(member)}
                                disabled={loading}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No actions
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <APIKeyManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <BucketConfigManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Modules</Typography>
              {!isAdmin && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You need admin or owner role to enable or disable modules for this organization.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={!!currentOrganization.threat_modeling_enabled}
                    onChange={async (_, checked) => {
                      if (!isAdmin || !currentOrganization.id) return;
                      try {
                        setLoading(true);
                        setError(null);
                        const updated = await updateOrganization(currentOrganization.id, { threat_modeling_enabled: checked });
                        dispatch(setCurrentOrganization({ ...currentOrganization, ...updated }));
                        setSuccess('Updated module settings');
                      } catch (err: any) {
                        console.error('Failed to update organization:', err);
                        setError(err.response?.data?.detail || 'Failed to update organization');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={!isAdmin || loading}
                  />
                }
                label="Threat Modeling"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={!!currentOrganization.dora_enabled}
                    onChange={async (_, checked) => {
                      if (!isAdmin || !currentOrganization.id) return;
                      try {
                        setLoading(true);
                        setError(null);
                        const updated = await updateOrganization(currentOrganization.id, { dora_enabled: checked });
                        dispatch(setCurrentOrganization({ ...currentOrganization, ...updated }));
                        setSuccess('Updated module settings');
                      } catch (err: any) {
                        console.error('Failed to update organization:', err);
                        setError(err.response?.data?.detail || 'Failed to update organization');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={!isAdmin || loading}
                  />
                }
                label="DORA"
              />
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to Organization</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the email address of the user. If they haven't registered yet, they'll automatically get access when they sign up.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newMemberEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewMemberEmail(e.target.value)}
            disabled={loading}
            placeholder="user@example.com"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newMemberRole}
              label="Role"
              onChange={(e: SelectChangeEvent) => setNewMemberRole(e.target.value)}
              disabled={loading}
            >
              {ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={loading || !newMemberEmail.trim()}
          >
            Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Member Role</DialogTitle>
        <DialogContent>
          {editingMember && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Updating role for: {editingMember.name || editingMember.user_id}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editRole}
                  label="Role"
                  onChange={(e: SelectChangeEvent) => setEditRole(e.target.value)}
                  disabled={loading}
                >
                  {ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={loading}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          {deletingMember && (
            <Typography>
              Are you sure you want to remove <Typography component="span" fontWeight="bold">{deletingMember.name || deletingMember.user_id}</Typography> from the organization?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveMember}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationManagement;

